import { Box, Button, Flex, Skeleton, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { RiDeleteBin6Line, RiEdit2Line } from "react-icons/ri";
import { Link as RouterLink } from "react-router-dom";
import { EmptyState } from "src/components/EmptyState";
import { GenericError } from "src/components/Feedback/GenericError";
import { AdminCard } from "src/components/Layout/AdminCard";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { deleteApiCredential } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useDeleteConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import ConnectSvg from "src/images/connect.svg?react";
import * as Url from "src/services/url/OrgAdmin";
import * as RD from "src/types/remoteData";

type Props = {
  doCreateDataService: () => void;
};

export const ApiCredentialsList: React.FC<Props> = ({
  doCreateDataService,
}) => {
  const organization = useOrganization();
  const organizationId = organization.toNullable()?.id;
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = React.useState<string>();
  const { confirm, confirmationDialog } = useDeleteConfirmationDialog({});

  const credentialsQuery = FSQ.useListApiCredentials(
    organization.hasData()
      ? { query: { organizationId: organization.data.id } }
      : { query: { organizationId: "" } },
    [organizationId],
    { enabled: organization.hasData() }
  );

  const userGroupsQuery = FSQ.useListUserGroups({}, [], {
    enabled: organization.hasData(),
  });

  const teamsQuery = FSQ.useListTeams(
    organization.hasData()
      ? { query: { organizationId: organization.data.id } }
      : { query: { organizationId: "" } },
    [organizationId],
    { enabled: organization.hasData() }
  );

  const remoteData = React.useMemo(
    () =>
      RD.toTuple3(
        RD.fromRestResult(credentialsQuery),
        RD.fromRestResult(userGroupsQuery),
        RD.fromRestResult(teamsQuery)
      )
        .map(([credentialsResponse, userGroupsResponse, teamsResponse]) => {
          const userGroupById = new Map(
            userGroupsResponse.data.userGroups.map((userGroup) => [
              userGroup.id,
              userGroup,
            ])
          );
          const teamById = new Map(
            teamsResponse.data.teams.map((team) => [team.id, team])
          );

          return {
            apiCredentials: credentialsResponse.data.map((apiCredential) => ({
              ...apiCredential,
              userGroup: userGroupById.get(apiCredential.userGroupId),
              team: apiCredential.teamId
                ? teamById.get(apiCredential.teamId)
                : undefined,
            })),
          };
        })
        .mapError(RD.errorResponseToError)
        .mapError(
          (error) =>
            error ??
            new Error("Failed to load API credentials. Please try again.")
        ),
    [credentialsQuery, userGroupsQuery, teamsQuery]
  );

  const onDelete = React.useCallback(
    async (clientId: string) => {
      if (!(await confirm())) return;

      setDeleting(clientId);
      try {
        await deleteApiCredential({ path: { clientId } });
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [FSQ.useListApiCredentialsKey],
          }),
          queryClient.invalidateQueries({
            queryKey: [FSQ.useGetApiCredentialKey],
          }),
        ]);
        toast({
          id: "delete-api-credential",
          title: "API credential deleted",
          isClosable: true,
        });
      } catch {
        toast.error({
          id: "delete-api-credential",
          title: "Delete failed!",
          description:
            "If the API credential have been used, then it cannot be deleted.",
          isClosable: true,
        });
      } finally {
        setDeleting(undefined);
      }
    },
    [confirm, queryClient, toast]
  );

  return (
    <>
      {confirmationDialog}
      <GQLRemoteDataView
        remoteData={remoteData}
        loading={<LoadingSkeleton />}
        reloading={null}
        error={() => (
          <GenericError message="Failed to load API credentials. Please try again." />
        )}
      >
        {({ apiCredentials }) => {
          if (apiCredentials.length === 0) {
            return (
              <Flex
                alignItems="center"
                justifyContent="center"
                minHeight="50vh"
              >
                <EmptyState
                  Svg={ConnectSvg}
                  heading="No API credentials"
                  description={
                    <WithUserPermissions
                      permissions={["data_services:create"]}
                      loadingIndicator={<ButtonSkeleton />}
                    >
                      <Box marginTop="4">
                        <Button
                          onClick={doCreateDataService}
                          variant="outline"
                          colorScheme="gray"
                        >
                          Create data service
                        </Button>
                      </Box>
                    </WithUserPermissions>
                  }
                />
              </Flex>
            );
          }

          return (
            <Flex gap="4" direction="column" paddingTop="6">
              {apiCredentials.map((apiCredential) => {
                const userGroup = apiCredential.userGroup;
                const role = userGroup?.label ?? userGroup?.name;
                const team = apiCredential.team;
                const isDeleting = deleting === apiCredential.clientId;

                return (
                  <AdminCard
                    key={apiCredential.clientId}
                    description={apiCredential.clientId}
                    title={
                      apiCredential.clientName || (
                        <Text variant="placeholder">No name</Text>
                      )
                    }
                    actions={
                      <Flex gap="2">
                        <WithUserPermissions
                          permissions={["data_services:update"]}
                          loadingIndicator={<ButtonSkeleton />}
                        >
                          <Button
                            as={RouterLink}
                            leftIcon={<RiEdit2Line />}
                            variant="outline"
                            colorScheme="gray"
                            isDisabled={isDeleting}
                            to={organization
                              .map((o) =>
                                Url.ApiCredentials.edit(
                                  o,
                                  apiCredential.clientId
                                )
                              )
                              .withDefault("#")}
                          >
                            Edit
                          </Button>
                        </WithUserPermissions>
                        <WithUserPermissions
                          permissions={["data_services:delete"]}
                          loadingIndicator={<ButtonSkeleton />}
                        >
                          <Button
                            leftIcon={<RiDeleteBin6Line />}
                            variant="outline"
                            colorScheme="gray"
                            isLoading={isDeleting}
                            onClick={() => onDelete(apiCredential.clientId)}
                          >
                            Delete
                          </Button>
                        </WithUserPermissions>
                      </Flex>
                    }
                  >
                    <Flex direction="column" gap="4">
                      <Flex direction="column">
                        <Text fontSize="sm" color="gray.700">
                          Role
                        </Text>
                        {role ? (
                          <Text color="gray.500">{role}</Text>
                        ) : (
                          <GenericError
                            variant="inline"
                            message="Missing role"
                            padding="0"
                          />
                        )}
                      </Flex>
                      {team ? (
                        <Flex direction="column">
                          <Text fontSize="sm" color="gray.700">
                            Team
                          </Text>
                          <Text color="gray.500">{team.name}</Text>
                        </Flex>
                      ) : null}
                    </Flex>
                  </AdminCard>
                );
              })}
            </Flex>
          );
        }}
      </GQLRemoteDataView>
    </>
  );
};

const LoadingSkeleton = () => {
  return (
    <Flex direction="column" gap="4" paddingTop="6">
      <Skeleton height="10rem" width="100%" />
      <Skeleton height="10rem" width="100%" />
    </Flex>
  );
};

const ButtonSkeleton = () => {
  return <Skeleton height="3rem" width="10rem" />;
};
